import { useEffect, useRef } from "react";

import { useAppDispatch, useAppSelector } from "@/store";
import {
  retryWalletSync,
  setWalletSyncPending,
  setWalletSyncTimedOut,
} from "@/store/slices/commonSlice";
import { fetchOfferRedemptionCounts } from "@/store/slices/offersSlice";
import { syncCustomerContextByPhone } from "@/store/thunks/auth";

/**
 * Module **93057** — the wallet sync. Mounted on BOTH menu routes (`/menu` and
 * `/menu/dine-in` each call it directly), which is why it lives on the shared
 * `MenuScreen` here.
 *
 * ## What it is for
 *
 * A customer's balances change on the SERVER when an order lands: points
 * accrue, cashback accrues, a voucher is spent, `total_orders` and
 * `total_spent` move. Nothing pushes that back to the browser — the storefront
 * has no socket — so the original polls for it, and only in the one window
 * where it matters: just after an order, on the way back to the menu.
 *
 * `/orders/[id]`'s back action already sends the customer to
 * `…/menu?…&refresh_customer=1` (`C`, `fmt_pages_orders_id.js`). That query
 * flag was the ONLY half of this feature this tree had: nothing read it, so
 * every balance stayed stale until a full reload. This is the reader.
 *
 * ## The original, verbatim
 *
 * ```js
 * function d() {
 *   let { intervalMs: t = 5e3, timeoutMs: n = 21e3 } = arguments[0] ?? {};
 *   …
 *   useEffect(() => {                                  // the trigger
 *     let e = new URLSearchParams(window.location.search);
 *     if ("1" !== e.get(QUERY.refreshCustomer)) return;
 *     e.delete(QUERY.refreshCustomer);
 *     …window.history.replaceState(…, "", url);
 *     (!x || g) && d(startWalletSync(Date.now()));
 *   }, [d, x, g]);
 *
 *   useEffect(() => {                                  // the poll
 *     if (!u || !x || g || !c) return;                 // authCheck, pending, !timedOut, loggedIn
 *     let e = p?.trim(); if (!e) return;               // and a phone number
 *     …
 *     l = setInterval(async () => {
 *       if (!r && !m.current) { m.current = !0;
 *         try { let t = await Promise.allSettled([
 *                 d(fetchCustomerByPhone(e)).unwrap(),
 *                 d(fetchOffersRedemptionCounts()).unwrap() ]);
 *               if (r) return;
 *               t.every(e => "fulfilled" === e.status) && (h(), d(completeWalletSync(Date.now())));
 *         } finally { m.current = !1; } }
 *     }, t);
 *     s = setTimeout(() => { r || (h(), d(timeoutWalletSync())); }, n);
 *     return () => { r = !0; m.current = !1; h(); };
 *   }, [d, p, c, x, g, h, u, t, n]);
 * }
 * ```
 *
 * `m` is the in-flight guard: a slow round trip must not stack a second one on
 * the next 5s tick. `r` is the unmount flag, checked AFTER the await so a
 * resolution that arrives late cannot complete a sync the component no longer
 * owns.
 *
 * ## Two recorded deviations
 *
 * 1. **No `walletSyncNonce`.** The original increments one on every start so
 *    the poll effect re-arms even when `walletSyncPending` was already true.
 *    Here `Retry` is the only re-entry and it is rendered only while
 *    `walletSyncTimedOut` is true, so flipping that flag back to false is
 *    itself the dependency change that re-arms the effect. The nonce would be
 *    a dep that never differs.
 * 2. **The three actions carry this slice's own names.** `retryWalletSync` is
 *    `startWalletSync` (pending true, timed-out false),
 *    `setWalletSyncPending(false)` is `completeWalletSync` (both false) and
 *    `setWalletSyncTimedOut(true)` is `timeoutWalletSync` (pending is already
 *    true whenever it can fire). The timestamps the original stores with them
 *    — `walletSyncStartedAt` / `lastWalletSyncAt` — are written and never read
 *    by anything in the bundle, so they are not modelled.
 */
export default function useWalletSync({
  intervalMs = 5000,
  timeoutMs = 21000,
}: { intervalMs?: number; timeoutMs?: number } = {}) {
  const dispatch = useAppDispatch();
  const isLoggedIn = useAppSelector((state) => state.auth.isLoggedIn);
  /**
   * The original reads `customer.phone_number`. This deployment's
   * `/web/auth/session` and `/web/auth/verify-otp` both answer with `phone`
   * (`phone: doc?.phone_number ?? phone`), so `phone_number` is undefined on
   * every real session here and reading only the original's spelling would
   * make this hook permanently inert. Both names are on the `Customer` type for
   * exactly this reason; `AuthBootstrap` reads `phone` too.
   */
  const phone = useAppSelector(
    (state) => state.auth.customer?.phone_number ?? state.auth.customer?.phone,
  );
  const { isAuthCheckComplete, walletSyncPending, walletSyncTimedOut } =
    useAppSelector((state) => state.common);
  const inFlight = useRef(false);

  /** The trigger: `?refresh_customer=1`, consumed and stripped from the URL. */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("refresh_customer") !== "1") return;
    params.delete("refresh_customer");
    const query = params.toString();
    const url = query.length > 0
      ? `${window.location.pathname}?${query}`
      : window.location.pathname;
    window.history.replaceState(
      { ...window.history.state, as: url, url },
      "",
      url,
    );
    if (!walletSyncPending || walletSyncTimedOut) dispatch(retryWalletSync());
  }, [dispatch, walletSyncPending, walletSyncTimedOut]);

  /** The poll, and its 21-second ceiling. */
  useEffect(() => {
    if (!isAuthCheckComplete || !walletSyncPending || walletSyncTimedOut) return;
    if (!isLoggedIn) return;
    const trimmed = phone?.trim();
    if (!trimmed) return;

    let cancelled = false;
    let poll: number | null = null;
    let deadline: number | null = null;
    const stop = () => {
      if (poll != null) window.clearInterval(poll);
      if (deadline != null) window.clearTimeout(deadline);
      poll = null;
      deadline = null;
    };

    poll = window.setInterval(async () => {
      if (cancelled || inFlight.current) return;
      inFlight.current = true;
      try {
        const results = await Promise.allSettled([
          dispatch(syncCustomerContextByPhone(trimmed)).unwrap(),
          dispatch(fetchOfferRedemptionCounts()).unwrap(),
        ]);
        if (cancelled) return;
        if (results.every((result) => result.status === "fulfilled")) {
          stop();
          dispatch(setWalletSyncPending(false));
        }
      } finally {
        inFlight.current = false;
      }
    }, intervalMs);

    deadline = window.setTimeout(() => {
      if (cancelled) return;
      stop();
      dispatch(setWalletSyncTimedOut(true));
    }, timeoutMs);

    return () => {
      cancelled = true;
      inFlight.current = false;
      stop();
    };
  }, [
    dispatch,
    intervalMs,
    isAuthCheckComplete,
    isLoggedIn,
    phone,
    timeoutMs,
    walletSyncPending,
    walletSyncTimedOut,
  ]);
}
